import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState
} from "react";

import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";
import { useOneTrustContext } from "@/utils/onetrust-provider";

interface AnalyticsWebClientContextType {
    analyticsWebClientInstance: AnalyticsWebClient | null;
}

const getAnonIdUsedByFeatureGatesClientInSSR = () =>
    window.featureGateBootstrapValues.identifiers.analyticsAnonymousId;

const AnalyticsWebClientContext = createContext<AnalyticsWebClientContextType>({
    analyticsWebClientInstance: null
});

export const WacAnalyticsClientProvider = (
    props: PropsWithChildren<{
        analyticsWebClient: AnalyticsWebClient | null;
        location: Location;
    }>
) => {
    const [analyticsWebClientInstance, setAnalyticsWebClientInstance] =
        useState<AnalyticsWebClient | null>(null);
    const { analyticsOptOut } = useOneTrustContext();
    useEffect(() => {
        if (!props.analyticsWebClient) {
            return;
        }
        if (!analyticsOptOut) {
            // If the FeatureGates and analytics clients are using different identifiers,
            // it means that the client already had an anonymousId in localStorage and/or cookies,
            // but SSR was unable to use it, either because the client had blocked sending cookies,
            // or because there was some issue extracting it.
            // An identifier mismatch means we won't be able to include this session in the experiment metrics.
            // We capture an operational event here as a way to tell how much data we've lost in our experiment results.
            const featureGatesAnonId = getAnonIdUsedByFeatureGatesClientInSSR();
            if (
                featureGatesAnonId !== props.analyticsWebClient.getAnonymousId()
            ) {
                props.analyticsWebClient.sendOperationalEvent({
                    action: "mismatched",
                    actionSubject: "anonymousId",
                    source: "awcInitialization",
                    attributes: {
                        featureGatesAnonId,
                        route: props.location.pathname
                    }
                });
            }

            setAnalyticsWebClientInstance(props.analyticsWebClient);
            return;
        }

        const isEnableOperationalEventWhenOptedOut = checkFeatureFlagGate(
            featureFlags.gwp10416DisableNonOperationalAnalyticsGateUip
        );
        if (isEnableOperationalEventWhenOptedOut) {
            // Set client even when opted out so the listener can send operational events (e.g. page view)
            setAnalyticsWebClientInstance(props.analyticsWebClient);
        }
    }, [analyticsOptOut, props.analyticsWebClient, props.location.pathname]);
    return (
        <AnalyticsWebClientContext.Provider
            value={{ analyticsWebClientInstance }}
        >
            {props.children}
        </AnalyticsWebClientContext.Provider>
    );
};

export const useAnalyticsClient = () => {
    const context = useContext(AnalyticsWebClientContext);
    if (!context) {
        throw new Error(
            "calls to useAnalyticsClient must be wrapped in <WacAnalyticsClientProvider>!"
        );
    }

    return context.analyticsWebClientInstance;
};
